<template>
    <LoadingPanel
        :headline="t('common.componentCommentsHeadline')"
        :is-pending="isPending"
        :is-error="isError"
        :hide-content="commentsData.length === 0"
    >
        <template v-if="commentsData.length === 0" #context>
            <ColorBadge
                :color="StatusTypes[ConditionStatus.None].color"
                :text="t(StatusTypes[ConditionStatus.None].labelKey)"
                small
            />
        </template>
        <div v-if="commentsData.length !== 0" class="comments-section">
            <template v-for="(comment, index) in commentsData" :key="comment.id">
                <div class="comments-section__item-horizontal-list">
                    <div class="comments-section__item">
                        <div class="comments-section__item-title">
                            {{ t('conditionStatus.content.comments.table.headers.date') }}
                        </div>
                        <div class="comments-section__item-value">
                            {{ comment.date }}
                        </div>
                    </div>
                    <div class="comments-section__item">
                        <div class="comments-section__item-title">
                            {{ t('transformers.conditionStatus.content.comments.table.headers.kid') }}
                        </div>
                        <div class="comments-section__item-value">
                            {{ comment.userKid }}
                        </div>
                    </div>
                </div>
                <div class="comments-section__item">
                    <div class="comments-section__item-title">
                        {{ t('common.comment') }}
                    </div>
                    <div
                        v-if="comment.comment.title"
                        class="comments-section__item-value comments-section__item-value--important"
                    >
                        {{ comment.comment.title }}
                    </div>
                    <div class="comments-section__item-value">
                        {{ comment.comment.text }}
                    </div>
                </div>
                <div v-if="index !== commentsData.length - 1" class="comments-section__separator" />
            </template>
        </div>
        <EmptyStatePanelContent v-else />
    </LoadingPanel>
</template>

<script setup lang="ts">
import LoadingPanel from '@/components/panel/LoadingPanel.vue'
import { useI18n } from 'vue-i18n'
import { computed, toRefs } from 'vue'
import { format } from 'date-fns'
import { DATE_FORMAT } from '@/utils/date-utils'
import EmptyStatePanelContent from '@/components/states/EmptyStatePanelContent.vue'
import { ConditionStatus, StatusTypes } from '@/configuration/status/condition-status'
import ColorBadge from '@/components/badge/ColorBadge.vue'
import { useGeneratorGeneralCommentsQuery } from '@/api/queries/generators/general-comments/useGeneratorGeneralCommentsQuery'
import { ActionType, ActionTypes } from '@/configuration/actions/action-type'

type NormalizedCommentData = {
    id: number
    date: string
    comment: {
        title?: string
        text: string
    }
    userKid: string
}

const props = defineProps<{
    generatorId: string
}>()

const { generatorId } = toRefs(props)

const { t } = useI18n()

const { data, isPending, isError } = useGeneratorGeneralCommentsQuery(generatorId)

const commentsData = computed<NormalizedCommentData[]>(() => {
    if (!data.value) {
        return []
    }

    const loadedComments = data.value

    const normalizedComments: NormalizedCommentData[] = []

    loadedComments.forEach((comment) => {
        const commentDate = format(new Date(comment.createdAt ?? new Date()), DATE_FORMAT)
        const actionType = ActionTypes[comment.actionType as ActionType]?.labelKey

        normalizedComments.push({
            id: comment.id ?? -1,
            date: commentDate,
            comment: {
                title: actionType ? t(actionType) : undefined,
                text: comment.text ?? '-',
            },
            userKid: comment.createdBy ?? '-',
        })
    })

    return normalizedComments
})
</script>

<style scoped lang="scss">
.comments-section {
    display: flex;
    flex-direction: column;

    gap: $spacing-xs;

    &__item {
        break-inside: avoid;

        &--wide {
            grid-column: span 2 / span 2;
        }

        &-title {
            font-weight: 500;
            font-size: $default-font-size;
        }

        &-value {
            font-size: $default-font-size;

            &--important {
                font-weight: 500;
                margin-top: $spacing-3xs;
            }
        }

        &-meta {
            font-size: $default-font-size;
            color: $pbl-text-muted;
        }
    }

    &__item-horizontal-list {
        break-inside: avoid;

        display: grid;
        grid-template-columns: repeat(6, minmax(0, 1fr));
        gap: $spacing-l;
    }

    &__separator {
        height: 1px;
        width: 100%;
        background-color: $pbl-foreground-muted;
    }
}
</style>
